import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api, ApiError } from '@/api'
import type { ProfileUpdatePayload } from '@/api'
import { maskCpf, maskPhone } from '@/lib/format'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { Card } from '@/components/ui/Card'
import { TextField } from '@/components/ui/Field'
import {
  BriefcaseIcon,
  IdCardIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
} from '@/components/ui/icons'
import { EditableRow } from './EditableRow'

/** Aba "Dados pessoais": três blocos de linhas expansíveis. */
export function PersonalDataPage() {
  const { user, refresh } = useAuth()
  const { notify } = useToast()

  const [draft, setDraft] = useState<ProfileUpdatePayload>({})

  const mutation = useMutation({
    mutationFn: (payload: ProfileUpdatePayload) => api.auth.updateProfile(payload),
    onSuccess: async () => {
      await refresh()
      setDraft({})
      notify('Informação atualizada.', 'success')
    },
    onError: (error) => {
      notify(
        error instanceof ApiError ? error.message : 'Não foi possível salvar.',
        'error',
      )
    },
  })

  /** Envia só o campo editado — evita sobrescrever o resto do perfil. */
  function save(field: keyof ProfileUpdatePayload) {
    const value = draft[field]
    if (value === undefined) return Promise.resolve()
    return mutation.mutateAsync({ [field]: value } as ProfileUpdatePayload)
  }

  function set(field: keyof ProfileUpdatePayload, value: string) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  // `min-w-0` até `md`: deixa o campo encolher para caber na mesma linha do
  // botão. Sem isso a largura mínima empurraria o botão para a linha de baixo.
  const fieldClass = 'min-w-0 flex-1 md:min-w-[200px]'

  return (
    <>
      <Card>
        <h2 className="px-5 py-4 text-[17px] font-semibold text-ink lg:text-[19px]">Dados pessoais</h2>

        <EditableRow
          icon={UserIcon}
          value={user?.nickname}
          description="Nome de preferência"
          submitting={mutation.isPending}
          onSubmit={() => save('nickname')}
        >
          <TextField
            label="Apelido"
            className={fieldClass}
            value={draft.nickname ?? user?.nickname ?? ''}
            onChange={(event) => set('nickname', event.target.value)}
          />
        </EditableRow>

        <EditableRow
          icon={IdCardIcon}
          value={user?.cpf}
          description="Número do CPF"
          submitting={mutation.isPending}
          onSubmit={() => save('cpf')}
        >
          <TextField
            label="CPF"
            inputMode="numeric"
            placeholder="000.000.000-00"
            className={fieldClass}
            value={draft.cpf ?? user?.cpf ?? ''}
            onChange={(event) => set('cpf', maskCpf(event.target.value))}
          />
        </EditableRow>

        <EditableRow
          icon={MapPinIcon}
          value={user?.address}
          description="Endereço fiscal"
          submitting={mutation.isPending}
          onSubmit={() => save('address')}
        >
          <TextField
            label="Endereço"
            placeholder="Rua, Bairro, Número, CEP"
            className={fieldClass}
            value={draft.address ?? user?.address ?? ''}
            onChange={(event) => set('address', event.target.value)}
          />
        </EditableRow>

        <EditableRow
          icon={IdCardIcon}
          value={
            [user?.firstName, user?.lastName].filter(Boolean).join(' ') || null
          }
          description="Nome e sobrenome"
          submitting={mutation.isPending}
          onSubmit={() =>
            mutation.mutateAsync({
              firstName: draft.firstName ?? user?.firstName ?? '',
              lastName: draft.lastName ?? user?.lastName ?? '',
            })
          }
        >
          <TextField
            label="Nome"
            autoComplete="given-name"
            className="min-w-0 flex-1 md:min-w-[140px]"
            value={draft.firstName ?? user?.firstName ?? ''}
            onChange={(event) => set('firstName', event.target.value)}
          />
          <TextField
            label="Sobrenome"
            autoComplete="family-name"
            className="min-w-0 flex-1 md:min-w-[140px]"
            value={draft.lastName ?? user?.lastName ?? ''}
            onChange={(event) => set('lastName', event.target.value)}
          />
        </EditableRow>
      </Card>

      <Card>
        <h2 className="px-5 py-4 text-[17px] font-semibold text-ink lg:text-[19px]">
          Ocupação e atividade
        </h2>

        <EditableRow
          icon={BriefcaseIcon}
          value={user?.occupation}
          description="Ocupação principal"
          submitting={mutation.isPending}
          onSubmit={() => save('occupation')}
        >
          <TextField
            label="Ocupação"
            className={fieldClass}
            value={draft.occupation ?? user?.occupation ?? ''}
            onChange={(event) => set('occupation', event.target.value)}
          />
        </EditableRow>
      </Card>

      <Card>
        <h2 className="px-5 py-4 text-[17px] font-semibold text-ink lg:text-[19px]">Dados da conta</h2>

        <EditableRow
          icon={PhoneIcon}
          value={user?.phone}
          description="Número pelo qual você recebe os códigos de verificação e comunicações"
          submitting={mutation.isPending}
          onSubmit={() => save('phone')}
        >
          <TextField
            label="Telefone"
            type="tel"
            inputMode="tel"
            placeholder="(00) 00000-0000"
            className={fieldClass}
            value={draft.phone ?? user?.phone ?? ''}
            onChange={(event) => set('phone', maskPhone(event.target.value))}
          />
        </EditableRow>

        <EditableRow
          icon={UserIcon}
          value={user?.username}
          description="Nome de usuário"
          submitting={mutation.isPending}
          onSubmit={() => save('username')}
        >
          <TextField
            label="Nome de usuário"
            autoComplete="username"
            className={fieldClass}
            value={draft.username ?? user?.username ?? ''}
            onChange={(event) => set('username', event.target.value)}
          />
        </EditableRow>
      </Card>
    </>
  )
}
